<!--
  告警配置 · 配置变更日志：筛选卡 + 表格卡 + 分页
  数据全部来自「告警配置变更日志」接口，请求 / 响应字段名与接口一致：
    请求 params：startTimeIso / endTimeIso / configObject / objectId / action
    响应 items：ts / time / configObject / objectId / objectName / action / beforeValue / afterValue / operatorId / operatorName
-->
<template>
  <div class="config-log-pane">
    <div class="card">
      <div class="card-header">
        <h3 style="margin-top: 0;">
          <span>配置变更日志</span>
          <span class="ch-pt">Log de Alterações da Configuração</span>
        </h3>
        <div class="hd-desc">
          <span>记录接收人、告警文案、监控州范围三类配置的变更留痕</span>
          <span class="desc-pt">
            Registro das alterações de destinatários, modelos de alerta e escopo de estados monitorados
          </span>
        </div>
      </div>

      <!-- 筛选 -->
      <el-form
        :model="searchParam" label-position="top"
        size="small" class="filter-form"
      >
        <el-row :gutter="16">
          <el-col :span="6">
            <el-form-item>
              <template slot="label">
                <bi-label zh="变更时间" pt="Data da Alteração" inline />
              </template>
              <el-date-picker
                v-model="searchParam.params.time"
                type="daterange"
                value-format="yyyy-MM-dd"
                range-separator="~"
                start-placeholder="开始日期 / Início"
                end-placeholder="结束日期 / Fim"
                style="width: 100%"
              />
            </el-form-item>
          </el-col>

          <el-col :span="6">
            <el-form-item>
              <template slot="label">
                <bi-label zh="配置类型" pt="Tipo de Configuração" inline />
              </template>
              <el-select
                v-model="searchParam.params.configObject" clearable placeholder="全部 / Todos"
                style="width: 100%"
              >
                <el-option
                  v-for="o in configObjectOptions" :key="o.value" :label="o.label + ' / ' + o.labelPt"
                  :value="o.value"
                />
              </el-select>
            </el-form-item>
          </el-col>

          <el-col :span="6">
            <el-form-item>
              <template slot="label">
                <bi-label zh="操作类型" pt="Tipo de Ação" inline />
              </template>
              <el-select
                v-model="searchParam.params.action" clearable placeholder="全部 / Todos"
                style="width: 100%"
              >
                <el-option
                  v-for="o in actionOptions" :key="o.value" :label="o.label + ' / ' + o.labelPt"
                  :value="o.value"
                />
              </el-select>
            </el-form-item>
          </el-col>

          <el-col :span="6">
            <el-form-item class="filter-btns">
              <template slot="label">
                <span class="ph-label">&nbsp;</span>
              </template>
              <el-button size="small" type="primary" @click="applyFilter">
                <bi-label zh="查询" pt="Consultar" inline />
              </el-button>
              <el-button size="small" @click="resetFilter">
                <bi-label zh="重置" pt="Redefinir" inline />
              </el-button>
            </el-form-item>
          </el-col>
        </el-row>
      </el-form>

      <!-- 表格 -->
      <el-table
        v-loading="tableLoading" :data="rows"
        border
        size="mini"
        style="width: 100%"
      >
        <el-table-column min-width="170" class-name="cell-num">
          <template slot="header">
            <bi-label zh="变更时间" pt="Data da Alteração" />
          </template>
          <template slot-scope="{ row }">{{ fmtLogTime(row) }}</template>
        </el-table-column>

        <el-table-column min-width="200">
          <template slot="header">
            <bi-label zh="配置类型" pt="Tipo de Configuração" />
          </template>
          <template slot-scope="{ row }">
            <span class="tag" :class="objCls(row.configObject)">
              {{ objZh(row.configObject) }}
              <span class="tag-pt">/ {{ objPt(row.configObject) }}</span>
            </span>
          </template>
        </el-table-column>

        <el-table-column min-width="170">
          <template slot="header">
            <bi-label zh="配置对象" pt="Objeto da Configuração" />
          </template>
          <template slot-scope="{ row }">
            <span>{{ objNameText(row) }}</span>
          </template>
        </el-table-column>

        <el-table-column min-width="120">
          <template slot="header">
            <bi-label zh="操作类型" pt="Tipo de Ação" />
          </template>
          <template slot-scope="{ row }">
            <span class="tag" :class="actCls(row.action)">
              {{ actZh(row.action) }}
              <span class="tag-pt">/ {{ actPt(row.action) }}</span>
            </span>
          </template>
        </el-table-column>

        <el-table-column min-width="220">
          <template slot="header">
            <bi-label zh="变更前" pt="Valor Anterior" />
          </template>
          <template slot-scope="{ row }">
            <el-tooltip
              class="item" effect="dark" :content="fmtSnapshot(row.beforeValue)"
              placement="top"
            >
              <template slot="content">
                <pre class="snap-cell">{{ fmtSnapshot(row.beforeValue) }}</pre>
              </template>
              <div>{{ row.beforeValue }}</div>
            </el-tooltip>
          </template>
        </el-table-column>

        <el-table-column min-width="220">
          <template slot="header">
            <bi-label zh="变更后" pt="Novo Valor" />
          </template>
          <template slot-scope="{ row }">
            <el-tooltip
              class="item" effect="dark" :content="fmtSnapshot(row.afterValue)"
              placement="top"
            >
              <template slot="content">
                <pre class="snap-cell">{{ fmtSnapshot(row.afterValue) }}</pre>
              </template>
              <div>{{ row.afterValue }}</div>
            </el-tooltip>
          </template>
        </el-table-column>

        <el-table-column min-width="140">
          <template slot="header">
            <bi-label zh="操作人" pt="Operador" />
          </template>
          <template slot-scope="{ row }">{{ operatorText(row) }}</template>
        </el-table-column>

      </el-table>

      <!-- 分页：共 N 条 + 每页条数 + 翻页 + 前往页 -->
      <div class="pagination">
        <el-pagination
          :current-page.sync="searchParam.pageNumber"
          :page-size.sync="searchParam.pageSize"
          :page-sizes="[10, 20, 50]"
          :total="total"
          layout="slot, sizes, prev, pager, next, jumper"
          class="cl-pagination"
          @current-change="handlePageChange"
          @size-change="handleSizeChange"
        >
          <span class="pb-total">
            共 {{ total }} 条<span class="pb-pt">/ Total: {{ total }} registros</span>
          </span>
        </el-pagination>
      </div>
    </div>
  </div>
</template>

<script>
import BiLabel from './BiLabel';
import {
  CONFIG_OBJECT_LABEL, CONFIG_ACTION_LABEL, todayStr
} from '../mock';
import { getInvoiceSubjectIeConfigChangePage } from '@/api/invoiceCenter';

export default {
  name: 'ConfigLogPane',
  components: { BiLabel },
  data() {
    return {
      // 查询参数结构同其它日志页：pageSize / pageNumber / params（params 键名与接口一致）
      searchParam: {
        pageSize: 10,
        pageNumber: 1,
        params: {
          // 时间区间默认当天
          time: [todayStr(), todayStr()],
          configObject: null,
          action: null
        }
      },
      total: 0,
      tableLoading: false,
      rows: []
    };
  },
  computed: {
    /** 配置类型选项：1接收人 2模板 3州范围 */
    configObjectOptions() {
      return Object.keys(CONFIG_OBJECT_LABEL).map(k => ({
        value: Number(k),
        label: CONFIG_OBJECT_LABEL[k].zh,
        labelPt: CONFIG_OBJECT_LABEL[k].pt
      }));
    },
    /** 操作类型选项：1新增 2编辑 3启停 4删除 */
    actionOptions() {
      return Object.keys(CONFIG_ACTION_LABEL).map(k => ({
        value: Number(k),
        label: CONFIG_ACTION_LABEL[k].zh,
        labelPt: CONFIG_ACTION_LABEL[k].pt
      }));
    }
  },
  mounted() {
    this.getTableData();
  },
  methods: {
    // ============ 接口 ============
    /**
     * 统一封装接口调用：失败只打日志并返回 null，由调用方提示
     * @param {string} label 接口名称（仅用于日志）
     * @param {Function} fn 真正的请求函数
     * @returns {Promise<object|null>} 成功返回 res，失败返回 null
     */
    async callApi(label, fn) {
      try {
        return await fn();
      } catch (e) {
        console.error(`[配置变更日志] ${label} 请求失败`, e);
        return null;
      }
    },
    /** 拉取配置变更日志：请求失败清空列表（空态）并提示，不做本地兜底 */
    async getTableData() {
      this.tableLoading = true;
      const param = {
        pageNumber: this.searchParam.pageNumber,
        pageSize: this.searchParam.pageSize,
        params: this.queryConditions()
      };
      const res = await this.callApi('page', () => getInvoiceSubjectIeConfigChangePage(param));
      if (res && res.code === 200) {
        const data = res.data || {};
        const list = Array.isArray(data.items) ? data.items : [];
        this.rows = list;
        // 接口 total 可能为 null（按游标翻页），此时用已加载条数兜底展示
        this.total = typeof data.total === 'number'
          ? data.total
          : (this.searchParam.pageNumber - 1) * this.searchParam.pageSize + list.length;
      } else {
        this.rows = [];
        this.total = 0;
        this.$message.error('配置变更日志接口请求失败，请稍后重试 / Falha ao consultar o log de configurações');
      }
      this.tableLoading = false;
    },

    // ============ 筛选 / 分页 ============
    /** 整理查询参数：字段名与接口一致，未填的条件置为 null */
    queryConditions() {
      const { time, configObject, action } = this.searchParam.params;
      const [start, end] = Array.isArray(time) ? time : [null, null];
      return {
        // 时间区间转 ISO（巴西时区 -03:00）：开始日 00:00:00，结束日 23:59:59
        startTimeIso: this.toIso(start, false),
        endTimeIso: this.toIso(end, true),
        configObject: configObject === null || configObject === '' ? null : configObject,
        // 页面暂不按配置对象 ID 筛选，固定传 null（接口字段保留，便于后续扩展）
        objectId: null,
        action: action === null || action === '' ? null : action
      };
    },
    /** 日期 → ISO 字符串（yyyy-MM-ddTHH:mm:ss-03:00）；空值返回 null */
    toIso(day, isEnd) {
      if (!day) return null;
      return `${day}T${isEnd ? '23:59:59' : '00:00:00'}-03:00`;
    },
    /** 点击查询：回到第 1 页重新请求 */
    applyFilter() {
      this.searchParam.pageNumber = 1;
      this.getTableData();
    },
    /** 重置：所有筛选项回到默认值（时间回到当天）并回到第 1 页 */
    resetFilter() {
      this.searchParam.params = {
        time: [todayStr(), todayStr()], configObject: null, action: null
      };
      this.searchParam.pageNumber = 1;
      this.getTableData();
    },
    /** 翻页：接口分页，切页时重新查询（不再本地切片） */
    handlePageChange(page) {
      this.searchParam.pageNumber = page;
      this.getTableData();
    },
    /** 切换每页条数：回到第 1 页重新查询 */
    handleSizeChange(size) {
      this.searchParam.pageSize = size;
      this.searchParam.pageNumber = 1;
      this.getTableData();
    },

    // ============ 单元格渲染 ============
    /** 配置类型中文（1接收人 2模板 3州范围） */
    objZh(v) {
      return (CONFIG_OBJECT_LABEL[v] && CONFIG_OBJECT_LABEL[v].zh) || '—';
    },
    /** 配置类型葡语 */
    objPt(v) {
      return (CONFIG_OBJECT_LABEL[v] && CONFIG_OBJECT_LABEL[v].pt) || '';
    },
    /** 配置类型标签配色 */
    objCls(v) {
      return (CONFIG_OBJECT_LABEL[v] && CONFIG_OBJECT_LABEL[v].cls) || 'tag-gray';
    },
    /** 操作类型中文（1新增 2编辑 3启停 4删除） */
    actZh(v) {
      return (CONFIG_ACTION_LABEL[v] && CONFIG_ACTION_LABEL[v].zh) || '—';
    },
    /** 操作类型葡语 */
    actPt(v) {
      return (CONFIG_ACTION_LABEL[v] && CONFIG_ACTION_LABEL[v].pt) || '';
    },
    /** 操作类型标签配色 */
    actCls(v) {
      return (CONFIG_ACTION_LABEL[v] && CONFIG_ACTION_LABEL[v].cls) || 'tag-gray';
    },
    /** 配置对象：优先 objectName，缺失时用 #objectId 兜底 */
    objNameText(row) {
      return row.objectName || (row.objectId ? `#${row.objectId}` : '—');
    },
    /** 操作人：无操作人信息时统一显示「系统」 */
    operatorText(row) {
      return row.operatorName || (row.operatorId ? String(row.operatorId) : '系统');
    },
    /**
     * 变更前 / 后：不翻译，后端返回什么展示什么，只做排版
     * JSON 字符串按 2 空格缩进换行展示（保留原始键名与取值）；非 JSON 原样展示；空值显示占位符
     */
    fmtSnapshot(value) {
      if (value === null || value === undefined || value === '') return '—';
      if (typeof value === 'object') return JSON.stringify(value, null, 2);
      try {
        const obj = JSON.parse(value);
        return obj && typeof obj === 'object' ? JSON.stringify(obj, null, 2) : String(value);
      } catch (e) {
        return String(value);
      }
    },

    // ============ 工具 ============
    /** 变更时间：优先毫秒时间戳 ts，其次 ISO 时间 time，格式化为 yyyy-MM-dd HH:mm:ss */
    fmtLogTime(row) {
      const src = row.ts ? new Date(Number(row.ts)) : (row.time ? new Date(row.time) : null);
      if (!src || isNaN(src.getTime())) return row.time || '—';
      const p = n => String(n).padStart(2, '0');
      return `${src.getFullYear()}-${p(src.getMonth() + 1)}-${p(src.getDate())} ${p(src.getHours())}:${p(src.getMinutes())}:${p(src.getSeconds())}`;
    }
  }
};
</script>

<style scoped lang="scss">
.config-log-pane {
  padding: 4px 0 20px;
}
.card {
  background: #fff;
  border-radius: 8px;
  padding: 16px;
}
.card-header {
  margin-bottom: 12px;
}
.card-header h3 {
  font-size: 15px;
  font-weight: 600;
  display: flex;
  flex-direction: column;
  margin-bottom: 0;
}
.card-header h3 .ch-pt {
  font-size: 12px;
  font-weight: 400;
  color: #86909c;
  margin-top: 2px;
}
.hd-desc {
  font-size: 12px;
  color: #86909c;
  margin-top: 4px;
  display: flex;
  flex-direction: column;
}
.hd-desc .desc-pt {
  font-size: 12px;
}

.filter-form {
  margin-bottom: 12px;
}
::v-deep .el-form-item { margin-bottom: 0; }
::v-deep .el-form-item__label { padding-bottom: 4px; line-height: 20px; }
.ph-label { display: block; }
::v-deep .filter-btns .el-form-item__content { display: flex; }
/* 日期区间选择器宽度自适应（默认较宽，会挤掉同一行的其他项） */
::v-deep .el-date-editor--daterange.el-input__inner { width: 100%; }

::v-deep .cell-num .cell {
  font-family: ui-monospace, 'SF Mono', Menlo, Consolas, monospace;
  font-size: 14px;
}
::v-deep .el-table th .cell { line-height: 1.35; }

/* 变更前 / 后：后端原始值只做排版（JSON 缩进换行），不翻译 */
.snap-cell {
  margin: 0;
  font-family: ui-monospace, 'SF Mono', Menlo, Consolas, monospace;
  font-size: 12px;
  line-height: 1.5;
  white-space: pre-wrap;
  word-break: break-all;
}

.tag {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  font-size: 12px;
  font-weight: 500;
  border-radius: 4px;
  line-height: 18px;
  white-space: nowrap;
}
.tag .tag-pt {
  font-size: 12px;
  font-weight: 400;
}
.tag-ok { background: #e8f7e9; color: #00b42a; }
.tag-err { background: #ffecec; color: #f53f3f; }
.tag-warn { background: #fff3e0; color: #ff7d00; }
.tag-info { background: #e6f0ff; color: #165dff; }
.tag-gray { background: #f2f3f5; color: #4e5969; }
.tag-brand { background: #f0eaff; color: #6b4eff; }

.tbl-empty {
  padding: 24px 0;
  font-size: 12px;
  color: #86909c;
}
.tbl-empty .empty-pt {
  display: block;
  font-size: 12px;
  margin-top: 2px;
}

/* 分页：el-pagination（总数走自定义 slot，保留中葡双语） */
.cl-pagination {
  padding: 14px 0 2px;
  font-size: 12px;
  color: #4e5969;
}
.pb-total {
  display: flex;
  flex-direction: column;
  font-weight: 400;
  line-height: 1.35;
}
.pb-total .pb-pt {
  font-size: 12px;
  color: #86909c;
}
::v-deep .cl-pagination .el-pagination__sizes { margin: 0 8px 0 12px; }
::v-deep .cl-pagination .btn-prev,
::v-deep .cl-pagination .btn-next,
::v-deep .cl-pagination .el-pager li,
::v-deep .cl-pagination .el-input__inner {
  background: #fff;
  border: 1px solid #e5e6eb;
  border-radius: 4px;
  min-width: 28px;
  height: 28px;
  line-height: 26px;
  margin: 0 3px;
  font-weight: 400;
}
::v-deep .cl-pagination .el-pager li.active {
  background: #00a551;
  border-color: #00a551;
  color: #fff;
}
::v-deep .cl-pagination .el-pagination__jump { margin-left: 8px; }
</style>
